import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Box,
  Card,
  alpha,
  Fade,
  TableSortLabel,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  TrendingUp,
  CalendarMonth,
  AttachMoney,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { formatDate, formatRevenue } from "../../../utils/formatters";

const LeadRevenueTable = ({ revenue = [] }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [orderBy, setOrderBy] = useState("paymentDate");
  const [order, setOrder] = useState("desc");


  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedRevenue = [...revenue].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    if (orderBy === "amount") {
      return order === "asc" ? aValue - bValue : bValue - aValue;
    }

    if (orderBy === "paymentDate" || orderBy === "month") {
      const dateA = new Date(a.paymentDate || 0);
      const dateB = new Date(b.paymentDate || 0);
      return order === "asc" ? dateA - dateB : dateB - dateA;
    }

    return 0;
  });

  // Calculate totals by currency
  const totalsByCurrency = revenue.reduce((acc, r) => {
    const curr = r.currency || "INR";
    acc[curr] = (acc[curr] || 0) + r.amount;
    return acc;
  }, {});

  return (
    <Fade in timeout={700}>
      <Box>
        {/* Revenue Table */}
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 3,
              // bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
              borderBottom: "1px solid",
              borderColor: "divider",
              // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "white",
                  mr: 2,
                }}
              >
                <TrendingUp color="inherit" />
              </Box> */}
              <Box>
                <Typography variant="h6" fontWeight={600} color="black">
                  Revenue History
                </Typography>
                <Typography variant="body2" color="black">
                  Detailed monthly revenue breakdown
                </Typography>
              </Box>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.grey[100], 0.5),
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, width: "20%" }}>
                    <TableSortLabel
                      active={orderBy === "month"}
                      direction={orderBy === "month" ? order : "asc"}
                      onClick={() => handleSort("month")}
                    >
                      Payment Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, width: "20%" }}
                    align="right"
                  >
                    <TableSortLabel
                      active={orderBy === "amount"}
                      direction={orderBy === "amount" ? order : "asc"}
                      onClick={() => handleSort("amount")}
                    >
                      Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "15%" }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "25%" }}>
                    Notes
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRevenue.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            bgcolor: (theme) =>
                              alpha(theme.palette.grey[400], 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                          }}
                        >
                          <TrendingUp
                            sx={{ fontSize: 40, color: "text.disabled" }}
                          />
                        </Box>
                        <Typography variant="h6" color="text.secondary">
                          No Revenue Records Found
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.disabled"
                          sx={{ mt: 1 }}
                        >
                          Revenue data will appear here once added
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRevenue.map((r, index) => (
                    <React.Fragment key={r._id}>
                      <TableRow
                        hover
                        sx={{
                          "&:hover": {
                            bgcolor: (theme) =>
                              alpha(theme.palette.primary.main, 0.02),
                          },
                          transition: "background-color 0.2s",
                          cursor: r.notes ? "pointer" : "default",
                        }}
                        onClick={() =>
                          r.notes &&
                          setExpandedRow(expandedRow === index ? null : index)
                        }
                      >
                        <TableCell>
                          <Chip
                            label={formatDate(r.paymentDate)}
                            size="small"
                            color="primary"
                            variant="outlined"
                            icon={
                              <CalendarMonth sx={{ fontSize: 16, pr: 1 }} />
                            }
                            sx={{
                              px: 2,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="success.main"
                          >
                            {formatRevenue(r.amount, r.currency)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={r.renewalStatus || "1st Payment"}
                            size="small"
                            color={r.renewalStatus?.includes("Renewal") ? "success" : "info"}
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 200,
                            }}
                          >
                            {r.notes || "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Expandable Notes Row */}
                      {r.notes && (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            sx={{
                              py: 0,
                              border: 0,
                            }}
                          >
                            <Collapse
                              in={expandedRow === index}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: (theme) =>
                                    alpha(theme.palette.info.main, 0.03),
                                  borderTop: "1px solid",
                                  borderBottom: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={600}
                                  sx={{ mb: 1, display: "block" }}
                                >
                                  Additional Notes:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.primary"
                                >
                                  {r.notes}
                                </Typography>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer with Grouped Totals */}
          {Object.keys(totalsByCurrency).length > 0 && (
            <Box
              sx={{
                p: 2,
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.05),
                borderTop: "2px solid",
                borderColor: "success.main",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                  Grand Total
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                  {Object.entries(totalsByCurrency).map(([curr, amount]) => (
                    <Typography key={curr} variant="h6" fontWeight={700} color="success.main">
                      {formatRevenue(amount, curr)}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Card>
      </Box>
    </Fade>
  );
};

export default LeadRevenueTable;
